import BenchlingConfig from 'BenchlingConfig';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  GraphBuilderEdgeTemplate,
  GraphBuilderErrorType,
  GraphBuilderObjectType,
  GraphBuilderValidationError,
  GraphBuilderValidationResult,
  MaybeValidationError,
} from 'processes/graph-builder/validation/GraphBuilderConstants';
import {
  ensureChainedRouterLimit,
  ensureNoChainedRouters,
  ensureNoMultipleRouterOnlyPathsBetweenTaskNodes,
  ensureRouterNodeConnectedToNonEndNode,
  ensureRouterNodeDestinationNodesAreNotSameSchema,
  ensureRouterNodeHasOneIncomingEdge,
  ensureRouterNodeHasOneSourceSchema,
  ensureRouterNodeHasRouteConfiguredForEachEdge,
  ensureRouterNodeHasTwoOutgoingEdges,
} from 'processes/graph-builder-processes/validation/validateProcessRouterNodes';
import {detectCyclesWithNode} from 'util/graph-utils/detectCycles';
import getLoopIgnoringAncestorsAndEdges from 'util/graph-utils/getLoopIgnoringAncestorsAndEdges';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';

import doesFlowchartHaveRouterWithAndOrFunction from '../flowchart-viz/util/doesFlowchartHaveRouterWithAndOrFunction';
import ensureMergingNodesOnlyDependOnOneBranchPoint from './ensureMergingNodesOnlyDependOnOneBranchPoint';
import {
  ProcessFlowchartDesignSpace,
  ProcessFlowchartForValidation,
  ProcessGraphBuilderEdge,
  ProcessGraphBuilderNode,
  ProcessGraphBuilderNodeType,
} from './ProcessGraphBuilderConstants';
import {
  getEdgeDisplayName,
  getFlowchartNodeFromClientId,
  getFulfillingMapperConfigs,
  getInboundEdges,
  getNodesByClientId,
  getOutboundEdges,
  getTaskNodesUpstreamOfNode,
  getTemplateNodeFromClientId,
  getTemplateNodeFromSchemaId,
} from './ProcessGraphGetters';

const doesNodeListContainNodeByClientId = (clientId: string, nodeList: Array<{clientId: string}>): boolean =>
  nodeList.find((node) => node.clientId === clientId) != null;

const doesNodeListContainNodeBySchemaId = (schemaId: string, nodeList: Array<{schemaId: string}>): boolean =>
  nodeList.find((node) => node.schemaId === schemaId) != null;

interface ValidationCallback {
  nodeId?: string;
  edgeId?: string;
}

// Edge errors are shown on the destination node, if possible, as the current UI doesn't have a way
// to show errors on edges directly.
function getValidationErrorForEdge(
  flowchart: ProcessFlowchartForValidation,
  edge: ProcessGraphBuilderEdge,
  errorMessage: (callbackProps: ValidationCallback) => string,
  edgeErrorType: GraphBuilderErrorType
): GraphBuilderValidationError {
  const destinationNode = getFlowchartNodeFromClientId(flowchart, edge.toNodeId);
  const destinationNodeId = destinationNode?.clientId;
  return {
    objectId: destinationNodeId ?? edge.clientId,
    objectType: destinationNode ? GraphBuilderObjectType.NODE : GraphBuilderObjectType.EDGE,
    errorMessage: errorMessage({nodeId: destinationNodeId, edgeId: edge.clientId}),
    errorType: edgeErrorType,
  };
}

function edgeConnectsNodesInFlowchart(
  edge: ProcessGraphBuilderEdge,
  flowchart: ProcessFlowchartForValidation
): boolean {
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.fromNodeId);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.toNodeId);
  return (
    fromNode != null &&
    toNode != null &&
    doesNodeListContainNodeByClientId(fromNode.clientId, flowchart.nodes) &&
    doesNodeListContainNodeByClientId(toNode.clientId, flowchart.nodes)
  );
}

function ensureNodeIsInsideDesignSpace(
  node: ProcessGraphBuilderNode,
  designSpace: ProcessFlowchartDesignSpace
): MaybeValidationError {
  return node.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
    node.taskSchemaId !== designSpace.flowchartSchema.id &&
    !doesNodeListContainNodeBySchemaId(node.taskSchemaId, designSpace.availableNodes)
    ? {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: `This node is not available for this flowchart.`,
        errorType: GraphBuilderErrorType.NODE_NOT_AVAILABLE,
      }
    : null;
}

function ensureNodeMappersFulfilled(
  node: ProcessGraphBuilderNode,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace
): MaybeValidationError {
  // Root, router, and end nodes cannot be mapped to. So we don't need to validate that their mappers are fulfilled.
  if (
    node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
    node.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS ||
    node.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS
  ) {
    return null;
  }

  // The associated template node is guaranteed to exist by ensureNodeIsInsideDesignSpace. But we
  // double-check, in case the tests are out of order
  const templateNode = getTemplateNodeFromSchemaId(designSpace.availableNodes, node.taskSchemaId);
  if (templateNode == null) {
    return null;
  }

  // We don't throw an error if the node hasn't been connected yet (this error will be reported elsewhere)
  const incomingEdges = flowchart.edges.filter((edge) => edge.toNodeId === node.clientId);
  if (incomingEdges.length === 0) {
    return null;
  }

  const fulfillingMapperConfigs = getFulfillingMapperConfigs(node, flowchart, templateNode, incomingEdges);

  return fulfillingMapperConfigs != null
    ? null
    : {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: `This node is missing required paths.`,
        errorType: GraphBuilderErrorType.NODE_MISSING_REQUIRED_PATH,
      };
}

function ensureNodeIsConnected(
  node: ProcessGraphBuilderNode,
  nodes: Array<ProcessGraphBuilderNode>,
  edges: Array<ProcessGraphBuilderEdge>
): MaybeValidationError {
  let nodeError;
  switch (node.type) {
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      nodeError = {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'The flowchart output node is not properly connected in the flowchart.',
        errorType: GraphBuilderErrorType.OUTPUT_NODE_NOT_CONNECTED,
      };
      break;
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      nodeError = {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'The flowchart root node is not properly connected in the flowchart.',
        errorType: GraphBuilderErrorType.ROOT_NODE_NOT_CONNECTED,
      };
      break;
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
      nodeError = {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'Connect to 1 source node and at least 2 destination nodes to configure router.',
        errorType: GraphBuilderErrorType.ROUTER_NOT_CONNECTED,
      };
      break;
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      nodeError = {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'Connect this node by drawing a path to another node.',
        errorType: GraphBuilderErrorType.TASK_NODE_NOT_CONNECTED,
      };
      break;
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      nodeError = {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'Connect 1 source node to the end node.',
        errorType: GraphBuilderErrorType.END_NODE_NOT_CONNECTED,
      };
      break;
    default:
      nodeError = null;
  }
  // We give a special pass to one-node flowcharts
  const nodeIsConnected =
    nodes.length === 1 ||
    edges.find((edge) => edge.fromNodeId === node.clientId || edge.toNodeId === node.clientId) != null;
  return !nodeIsConnected ? nodeError : null;
}

function ensureFlowchartHasTaskNode(nodes: Array<ProcessGraphBuilderNode>): MaybeValidationError {
  const taskNode = nodes.find((node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_TASK);
  return taskNode == null
    ? {
        objectId: null,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: "This flowchart needs a node for the flowchart's task schema.",
        errorType: GraphBuilderErrorType.NO_FLOWCHART_TASK_NODE,
      }
    : null;
}

function ensureFlowchartHasIntermediateNodes(nodes: Array<ProcessGraphBuilderNode>): MaybeValidationError {
  const rootNode = nodes.find((node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_TASK);
  if (rootNode == null) {
    return null;
  }
  if (!nodes.some((node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_STEP)) {
    return {
      objectId: rootNode.clientId,
      objectType: GraphBuilderObjectType.NODE,
      errorMessage: 'This flowchart needs at least one step.',
      errorType: GraphBuilderErrorType.NO_INTERMEDIATE_NODES,
    };
  }
  return null;
}

function ensureFlowchartHasSingleEntryPoint(
  nodes: Array<ProcessGraphBuilderNode>,
  edges: Array<ProcessGraphBuilderEdge>
): Array<GraphBuilderValidationError> | null {
  const startNodes = nodes.filter((node) => edges.find(({toNodeId}) => toNodeId === node.clientId) == null);
  return startNodes.length > 1
    ? startNodes.map((startNode) => ({
        objectId: startNode.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'The flowchart cannot have more than one start node.',
        errorType: GraphBuilderErrorType.TOO_MANY_START_NODES,
      }))
    : null;
}

function ensureFlowchartHasOutputNode(
  nodes: Array<ProcessGraphBuilderNode>,
  designSpace: ProcessFlowchartDesignSpace
): {error: MaybeValidationError; outputNode: ProcessGraphBuilderNode | null} {
  // If the flowchart schema has outputs, we need an output node, and we need it to have an incoming edge
  const outputNode =
    nodes.find((node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_OUTPUT) ?? null;
  if (designSpace.flowchartSchema.outputSchema != null) {
    if (outputNode == null) {
      return {
        error: {
          objectId: null,
          objectType: GraphBuilderObjectType.NODE,
          errorMessage: "This flowchart needs a node for the flowchart's output schema.",
          errorType: GraphBuilderErrorType.NO_OUTPUT_NODE,
        },
        outputNode,
      };
    }
  }
  return {error: null, outputNode};
}

// This error should not be possible when using the UI
function ensureEdgeConnectsNodesInFlowchart(
  edge: ProcessGraphBuilderEdge,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  return !edgeConnectsNodesInFlowchart(edge, flowchart)
    ? getValidationErrorForEdge(
        flowchart,
        edge,
        () =>
          `Path '${getEdgeDisplayName(
            edge,
            flowchart
          )}' connects nodes that haven't been added to the flowchart.`,
        GraphBuilderErrorType.EDGE_CONNECTS_UNAVAILABLE_NODES
      )
    : null;
}

function ensureEdgeBetweenSchemasIsValid(
  fromNodeSchemaId: string,
  toNodeSchemaId: string,
  designSpace: ProcessFlowchartDesignSpace
): boolean {
  return designSpace.availableEdges.some((tEdge) => {
    const fromTemplateNode = getTemplateNodeFromClientId(designSpace.availableNodes, tEdge.fromNodeId);
    const toTemplateNode = getTemplateNodeFromClientId(designSpace.availableNodes, tEdge.toNodeId);

    return fromNodeSchemaId === fromTemplateNode?.schemaId && toNodeSchemaId === toTemplateNode?.schemaId;
  });
}

function ensureEdgeIsInsideDesignSpace(
  edge: ProcessGraphBuilderEdge,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace
): Array<GraphBuilderValidationError> | GraphBuilderValidationError | null {
  const edgeError = (): GraphBuilderValidationError =>
    getValidationErrorForEdge(
      flowchart,
      edge,
      () => `Path '${getEdgeDisplayName(edge, flowchart)}' is not available for this flowchart.`,
      GraphBuilderErrorType.EDGE_NOT_AVAILABLE
    );

  // We have to get the schemas for the target edge. This requires a few hops to connect the client IDs.
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.fromNodeId);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.toNodeId);
  if (fromNode == null || toNode == null) {
    return edgeError();
  }
  if (toNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    // Any node can have an edge to a router except for the flowchart output node.
    return fromNode.type === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS ? edgeError() : null;
  }
  if (toNode.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    const inboundEdges = getInboundEdges(toNode, flowchart.edges);
    const outboundEdges = getOutboundEdges(toNode, flowchart.edges);
    if (fromNode.type !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      return {
        objectId: toNode.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'End nodes can only be connected to 1 router node.',
        errorType: GraphBuilderErrorType.END_NODE_CONNECTED_TO_NON_ROUTER_NODE,
      };
    } else if (inboundEdges.length > 1) {
      return {
        objectId: toNode.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'An end node must be connected to exactly one source node.',
        errorType: GraphBuilderErrorType.END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES,
      };
    } else if (outboundEdges.length > 0) {
      return {
        objectId: toNode.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: 'An end node cannot have outgoing edges.',
        errorType: GraphBuilderErrorType.END_NODE_HAS_OUTBOUND_EDGES,
      };
    }
    return null;
  }
  if (fromNode.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    return edgeError();
  }
  // If the edge is from a router, we use the previous nodes to check that the edge is in the design space.
  if (fromNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    const taskNodesBeforeRouter = getTaskNodesUpstreamOfNode(fromNode.clientId, flowchart);
    for (const taskNode of taskNodesBeforeRouter) {
      if (!ensureEdgeBetweenSchemasIsValid(taskNode.taskSchemaId, toNode.taskSchemaId, designSpace)) {
        return [
          getValidationErrorForEdge(
            flowchart,
            edge,
            () => `The transition from ${taskNode.name} to ${toNode.name} is not available`,
            GraphBuilderErrorType.EDGE_NOT_AVAILABLE
          ),
          {
            objectId: fromNode.clientId,
            objectType: GraphBuilderObjectType.NODE,
            errorMessage: `One or more transitions through this router are not configured.`,
            errorType: GraphBuilderErrorType.ROUTER_EDGES_NOT_CONFIGURED,
          },
        ];
      }
    }
  } else if (!ensureEdgeBetweenSchemasIsValid(fromNode.taskSchemaId, toNode.taskSchemaId, designSpace)) {
    return edgeError();
  }

  return null;
}

// This represents a technical problem. The UI should of course prevent this error from occurring, but an
// automation in the future may produce duplicate edges.
function ensureNoDuplicateEdges(
  edge: ProcessGraphBuilderEdge,
  flowchart: ProcessFlowchartForValidation
): Array<GraphBuilderValidationError> | null {
  const errors: Array<GraphBuilderValidationError> = [];
  for (const scanEdge of flowchart.edges) {
    if (edge !== scanEdge && edge.fromNodeId === scanEdge.fromNodeId && edge.toNodeId === scanEdge.toNodeId) {
      errors.push(
        getValidationErrorForEdge(
          flowchart,
          edge,
          () => `Path '${getEdgeDisplayName(edge, flowchart)}' is a duplicate.`,
          GraphBuilderErrorType.DUPLICATE_EDGES
        )
      );
    }
  }
  return errors.length > 0 ? errors : null;
}

/**
 * This validation checks that for every loop in a flowchart, you can only "enter" at one distinct node.
 * For instance, in the following graph:
 *       2 -->
 *     /      \
 *    1        5 -> R1 -> 7
 *     \      /      \
 *      3 -> 4        6
 *       \           /
 *         <---------
 * there exists a loop (3 -> 4 -> 5 -> R1 -> 6 -> 3) with two entry points (node 3, via 1 -> 3, and node 5,
 * via 2 -> 5), which is disallowed.
 *
 * We disallow this because it complicates ancestor-finding. Because we track ancestry using a DFS that
 * stops when we detect a cycle, in the above graph, we might detect an ancestor of 3 that we would otherwise
 * ignore.
 *
 * If we traverse the path 1 -> 2 -> 5 -> R1 -> 6 -> 3, we set 5 as an ancestor of 3, because no cycle
 * has been detected. However, if we traverse the path 1 -> 3 -> 4 -> 5 -> R1 -> 6 -> 3, we set 3 as an
 * ancestor of 5, and seeing a loop, do not set 5 as an ancestor of 3. For LCA purposes, this would be correct.
 */
function ensureLoopsHaveOneEntryPoint(
  flowchart: ProcessFlowchartForValidation
): Array<GraphBuilderValidationError> | null {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_TASK
  );
  if (rootNode == null) {
    return null;
  }
  const adjacencyMap: AdjacencyMap = flowchart.edges.reduce(
    (adjacencyMap: AdjacencyMap, edge: GraphBuilderEdgeTemplate) => {
      adjacencyMap[edge.fromNodeId] ||= [];
      adjacencyMap[edge.fromNodeId].push(edge.toNodeId);
      return adjacencyMap;
    },
    {}
  );
  const {nodeIdsToAncestors} = getLoopIgnoringAncestorsAndEdges(rootNode.clientId, adjacencyMap);

  const nodesInLoop: Set<string> = new Set();
  // We iterate through each node's ancestors.
  // If node A has an ancestor, node B, who has also detected node A as its ancestor, we error on both node A and node B.
  for (const [nodeId, ancestors] of Object.entries(nodeIdsToAncestors)) {
    if (nodesInLoop.has(nodeId)) {
      // Stop early if we're already erroring on the node
      continue;
    }
    for (const ancestor of [...ancestors]) {
      if (ancestor !== nodeId && nodeIdsToAncestors[ancestor].has(nodeId)) {
        // We ignore the ancestor when it is the node itself
        nodesInLoop.add(nodeId);
        nodesInLoop.add(ancestor);
        break;
      }
    }
  }

  return [...nodesInLoop].map((nodeId) => ({
    objectId: nodeId,
    objectType: GraphBuilderObjectType.NODE,
    errorMessage: `Every loop can only have one starting point.`,
    errorType: GraphBuilderErrorType.LOOP_HAS_MULTIPLE_ENTRY_POINTS,
  }));
}

function ensureLoopsContainTaskAndRouterNode(
  node: ProcessGraphBuilderNode,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_TASK
  );
  if (rootNode == null) {
    return null;
  }

  const adjacencyMap: AdjacencyMap = flowchart.edges.reduce(
    (adjacencyMap: AdjacencyMap, edge: GraphBuilderEdgeTemplate) => {
      adjacencyMap[edge.fromNodeId] ||= [];
      adjacencyMap[edge.fromNodeId].push(edge.toNodeId);
      return adjacencyMap;
    },
    {}
  );

  const nodesByClientId = getNodesByClientId(flowchart);
  const cycles = detectCyclesWithNode(adjacencyMap, node.clientId);
  for (const cyclicNodes of cycles) {
    const hasRouterNode = Array.from(cyclicNodes).some(
      (clientId) => nodesByClientId[clientId].type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
    );
    const hasTaskNode = Array.from(cyclicNodes).some(
      (clientId) => nodesByClientId[clientId].type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS
    );
    if (!hasRouterNode || !hasTaskNode) {
      return {
        objectId: node.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: `Every loop in the flowchart must have at least one task and one router node.`,
        errorType: GraphBuilderErrorType.LOOP_MISSING_TASK_OR_ROUTER_NODE,
      };
    }
  }
  return null;
}

function ensureFlowchartDoesNotExceedMaxNumberOfNodes(
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessGraphBuilderNodeType.FLOWCHART_TASK
  );
  if (rootNode == null) {
    return null;
  }
  return flowchart.nodes.length > BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI
    ? {
        objectId: rootNode.clientId,
        objectType: GraphBuilderObjectType.NODE,
        errorMessage: `The number of nodes in a flowchart may not exceed ${BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI}`,
        errorType: GraphBuilderErrorType.MAX_NODES_LIMIT_EXCEEDED,
      }
    : null;
}

export default function validateProcessGraph(
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace,
  didInitialFlowchartHaveChainedRouters: boolean
): GraphBuilderValidationResult {
  let errors: Array<GraphBuilderValidationError> = [];
  const disconnectedNodeIds: Array<string> = [];
  const {nodes, edges} = flowchart;

  const pushErrors = (
    errorOrErrors: MaybeValidationError | Array<GraphBuilderValidationError> | null
  ): void => {
    if (errorOrErrors != null) {
      errors = errors.concat(errorOrErrors);
    }
  };

  pushErrors(ensureFlowchartDoesNotExceedMaxNumberOfNodes(flowchart));

  for (const node of nodes) {
    pushErrors(ensureNodeIsInsideDesignSpace(node, designSpace));
    const error = ensureNodeIsConnected(node, nodes, edges);
    if (error != null) {
      disconnectedNodeIds.push(node.clientId);
      pushErrors(error);
    } else if (node.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      pushErrors(ensureRouterNodeHasOneIncomingEdge(node, nodes, edges));
      pushErrors(ensureRouterNodeHasTwoOutgoingEdges(node, nodes, edges));
      pushErrors(ensureRouterNodeConnectedToNonEndNode(node, flowchart));
    }
    pushErrors(ensureNodeMappersFulfilled(node, flowchart, designSpace));
    pushErrors(ensureLoopsContainTaskAndRouterNode(node, flowchart));
    if (node.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      pushErrors(ensureChainedRouterLimit(node, flowchart));
      if (
        BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS &&
        (!didInitialFlowchartHaveChainedRouters ||
          (didInitialFlowchartHaveChainedRouters && doesFlowchartHaveRouterWithAndOrFunction(flowchart)))
      ) {
        pushErrors(ensureNoChainedRouters(node, flowchart));
      }
      pushErrors(ensureRouterNodeDestinationNodesAreNotSameSchema(node, flowchart));
      pushErrors(ensureRouterNodeHasRouteConfiguredForEachEdge(node, flowchart));
      pushErrors(ensureRouterNodeHasOneSourceSchema(node, flowchart, designSpace));
    } else if (node.type !== ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
      pushErrors(ensureNoMultipleRouterOnlyPathsBetweenTaskNodes(node, flowchart));
    }
  }

  pushErrors(ensureFlowchartHasTaskNode(nodes));
  pushErrors(ensureFlowchartHasIntermediateNodes(nodes));

  // If a flowchart has disconnected nodes, no need to also add this message
  if (disconnectedNodeIds.length === 0) {
    pushErrors(ensureFlowchartHasSingleEntryPoint(nodes, edges));
  }

  const {error} = ensureFlowchartHasOutputNode(nodes, designSpace);
  pushErrors(error);

  for (const edge of edges) {
    pushErrors(ensureEdgeIsInsideDesignSpace(edge, flowchart, designSpace));
    pushErrors(ensureEdgeConnectsNodesInFlowchart(edge, flowchart));
    pushErrors(ensureNoDuplicateEdges(edge, flowchart));
  }

  pushErrors(ensureLoopsHaveOneEntryPoint(flowchart));
  pushErrors(ensureMergingNodesOnlyDependOnOneBranchPoint(flowchart, designSpace));

  return {errors};
}
